import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { downloadInvoicePdf } from '../utils/invoicePdf';
import { paymentApi, dealerCreditApi } from '../services/api';
import StripeCardForm from '../components/StripeCardForm';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const fmt = (n: number) => `$${Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

type FilterTab = 'all' | 'unpaid' | 'paid' | 'overdue';

interface InvoiceOrder {
  id: string;
  orderNumber: string;
  invoiceNumber: string;
  sidemark: string;
  invoiceData: any;
  totalAmount: number;
  paymentAmount: number;
  achDiscountAmount: number;
  status: string;
  invoiceAdjusted: boolean;
  invoiceSentAt: string | null;
  shippingAdjustmentStatus: string | null;
  shippingAdjustmentAmount: number | null;
  shippingAdjustmentDueDate: string | null;
  createdAt: string;
}

export function InvoicesPage() {
  const [orders, setOrders] = useState<InvoiceOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // Multi-invoice payment state. Keyed by order id; value is the dollar amount
  // the dealer wants to apply to that invoice (defaults to current balance).
  const [selected, setSelected] = useState<Record<string, number>>({});
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [availableCredit, setAvailableCredit] = useState(0);
  const [creditToApply, setCreditToApply] = useState(0);
  const [surchargeEnabled, setSurchargeEnabled] = useState(false);
  const [savedCards, setSavedCards] = useState<Array<{ id: string; type: 'card' | 'us_bank_account'; brand?: string; last4?: string; expMonth?: number; expYear?: number; accountType?: string }>>([]);
  const [chargingSavedCardId, setChargingSavedCardId] = useState<string | null>(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInvoices();
    // Load available credit (silently fail — page works without it)
    dealerCreditApi.getMyAvailable()
      .then(r => setAvailableCredit(Number(r?.available || 0)))
      .catch(() => {});
    // Pull surcharge enabled state — disclosure banner is misleading when off
    fetch(`${API_BASE}/system-settings/pricing`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSurchargeEnabled(d.cc_surcharge_enabled === true); })
      .catch(() => {});
    // Saved Stripe cards (silently fail — modal just shows "enter card" if none)
    paymentApi.getStripeSavedMethods()
      .then(cards => setSavedCards(cards || []))
      .catch(() => {});
    // Handle Stripe redirect back from multi-pay checkout
    const params = new URLSearchParams(location.search);
    if (params.get('multi_pay') === 'success') {
      setSuccessBanner('Payment received — invoices will mark as paid shortly.');
      navigate('/invoices', { replace: true });
    } else if (params.get('multi_pay') === 'cancelled') {
      setPayError('Payment cancelled. Your selected invoices were not charged.');
      navigate('/invoices', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_BASE}/orders?invoice=true&pageSize=100&sortBy=created_at&sortOrder=desc`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to fetch invoices');
      const data = await resp.json();
      setOrders(data.data || data.items || []);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const getInvoiceTotal = (order: InvoiceOrder): number => {
    if (order.invoiceData) {
      const inv = typeof order.invoiceData === 'string' ? JSON.parse(order.invoiceData) : order.invoiceData;
      if (inv.totalAmount != null) return Number(inv.totalAmount);
    }
    return order.totalAmount || 0;
  };

  const getBalanceDue = (order: InvoiceOrder) => {
    const achDiscount = Number(order.achDiscountAmount || 0);
    // In-flight ACH (pending_ach_amount) is money already committed but not yet
    // settled — subtract it so a processing order shows no payable balance and
    // can't be re-paid. Dealers overpaid 3× while their ACH was clearing.
    const pendingAch = Number((order as any).pendingAchAmount || (order as any).pending_ach_amount || 0);
    // Use invoice data balance (after dealer discount) when available, overlay live payments
    if (order.invoiceData) {
      const inv = typeof order.invoiceData === 'string' ? JSON.parse(order.invoiceData) : order.invoiceData;
      const invoiceTotal = Number(inv.totalAmount || 0);
      const paid = Number(order.paymentAmount || 0);
      return invoiceTotal - paid - achDiscount - pendingAch;
    }
    return (order.totalAmount || 0) - (order.paymentAmount || 0) - achDiscount - pendingAch;
  };

  // An ACH is clearing on this order — show "Payment Pending", not "Paid".
  const isAchPending = (order: InvoiceOrder) =>
    Number((order as any).pendingAchAmount || (order as any).pending_ach_amount || 0) > 0.5;

  const getDueDate = (order: InvoiceOrder) => {
    if (order.invoiceData) {
      const inv = typeof order.invoiceData === 'string' ? JSON.parse(order.invoiceData) : order.invoiceData;
      return inv.dueDate || null;
    }
    return null;
  };

  const isOverdue = (order: InvoiceOrder) => {
    const balance = getBalanceDue(order);
    if (balance <= 0.5) return false;
    // The remaining balance on a deposit order is due BEFORE shipping/pickup, so
    // it cannot be "overdue" while the order is still in production. Only flag it
    // once the order is ready to ship or beyond — otherwise a net-terms invoice
    // due date makes an in-production balance look past-due.
    const balanceDueStatuses = [
      'ready_to_ship', 'shipped', 'delivered', 'installed',
      'install_scheduled', 'completed', 'closed',
    ];
    if (!balanceDueStatuses.includes((order.status || '').toLowerCase())) return false;
    const dueDate = getDueDate(order);
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const isPaid = (order: InvoiceOrder) => getBalanceDue(order) <= 0.5;

  const handleDownloadPdf = async (orderId: string, invoiceNumber?: string) => {
    if (downloadingId) return;
    try {
      setDownloadingId(orderId);
      await downloadInvoicePdf(orderId, invoiceNumber);
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  const toggleSelected = (order: InvoiceOrder) => {
    const balance = Math.max(0, Math.round(getBalanceDue(order) * 100) / 100);
    setPayError(null);
    setSelected(prev => {
      const next = { ...prev };
      if (next[order.id] != null) delete next[order.id];
      else next[order.id] = balance;
      return next;
    });
  };

  const updateSelectedAmount = (orderId: string, value: string) => {
    const parsed = parseFloat(value);
    setSelected(prev => ({ ...prev, [orderId]: isNaN(parsed) ? 0 : Math.max(0, parsed) }));
  };

  const selectedTotal = Object.values(selected).reduce((s, v) => s + (Number(v) || 0), 0);
  const selectedCount = Object.keys(selected).length;

  /** Validate selections + open the Stripe Elements card modal. */
  const handlePaySelected = () => {
    setPayError(null);
    if (selectedCount === 0) return;
    for (const [orderId, amount] of Object.entries(selected)) {
      const order = orders.find(o => o.id === orderId);
      if (!order) continue;
      const balance = Math.round(getBalanceDue(order) * 100) / 100;
      if (amount < 1) {
        setPayError(`${order.invoiceNumber || order.orderNumber}: amount must be at least $1.00`);
        return;
      }
      if (amount > balance + 0.01) {
        setPayError(`${order.invoiceNumber || order.orderNumber}: amount $${amount.toFixed(2)} exceeds balance $${balance.toFixed(2)}`);
        return;
      }
    }
    // Auto-suggest applying credit up to selected total (capped at avail) —
    // dealer can edit it down. Leave them >=$1 to charge (PaymentIntent min).
    if (availableCredit > 0) {
      const suggested = Math.min(availableCredit, Math.max(0, selectedTotal - 1));
      setCreditToApply(Math.round(suggested * 100) / 100);
    } else {
      setCreditToApply(0);
    }
    setShowCardModal(true);
  };

  /** Build the {orderId, amount} payload from current selections. */
  const buildPaymentsPayload = (): Array<{ orderId: string; amount: number }> => {
    return Object.entries(selected).map(([orderId, amount]) => ({
      orderId,
      amount: Math.round(amount * 100) / 100,
    }));
  };

  const filtered = orders.filter(order => {
    if (activeTab === 'paid') return isPaid(order);
    if (activeTab === 'unpaid') return !isPaid(order) && !isOverdue(order);
    if (activeTab === 'overdue') return isOverdue(order);
    return true;
  });

  const totalOutstanding = orders
    .filter(o => !isPaid(o))
    .reduce((sum, o) => sum + getBalanceDue(o), 0);

  const overdueCount = orders.filter(o => isOverdue(o)).length;

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: `All (${orders.length})` },
    { key: 'unpaid', label: `Unpaid (${orders.filter(o => !isPaid(o) && !isOverdue(o)).length})` },
    { key: 'overdue', label: `Overdue (${overdueCount})` },
    { key: 'paid', label: `Paid (${orders.filter(o => isPaid(o)).length})` },
  ];

  const renderStatusBadge = (order: InvoiceOrder) => {
    // Show ACH-in-flight BEFORE "Paid"/"Unpaid" so the dealer knows a payment is
    // clearing and doesn't pay again.
    if (isAchPending(order)) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">Payment Pending</span>;
    }
    if (isPaid(order)) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Paid</span>;
    }
    if (isOverdue(order)) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Overdue</span>;
    }
    if (order.shippingAdjustmentStatus === 'pending') {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">Adjustment Pending</span>;
    }
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Unpaid</span>;
  };

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-6">My Invoices</h1>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
          <span className="ml-3 text-gray-500">Loading invoices...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">My Invoices</h1>
          <p className="text-sm text-gray-500 mt-1">View and pay your invoices · select multiple to pay in one transaction</p>
        </div>
      </div>

      {successBanner && (
        <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded mb-4 text-sm">{successBanner}</div>
      )}
      {payError && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded mb-4 text-sm">{payError}</div>
      )}

      {/* Stats */}
      {orders.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="card">
            <p className="text-sm text-gray-500">Total Invoices</p>
            <p className="text-2xl font-bold text-white">{orders.length}</p>
          </div>
          <div className="card">
            <p className="text-sm text-gray-500">Outstanding Balance</p>
            <p className="text-2xl font-bold text-amber-600">{fmt(totalOutstanding)}</p>
          </div>
          <div className="card">
            <p className="text-sm text-gray-500">Overdue</p>
            <p className={`text-2xl font-bold ${overdueCount > 0 ? 'text-red-600' : 'text-green-600'}`}>{overdueCount}</p>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 rounded-lg p-1 w-fit" style={{ backgroundColor: 'var(--bg-hover)' }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm rounded-md transition font-medium`}
            style={{
              backgroundColor: activeTab === tab.key ? 'var(--bg-card)' : 'transparent',
              color: activeTab === tab.key ? 'var(--text-primary)' : 'var(--text-secondary)',
              boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {orders.length === 0 && (
        <div className="card p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-500 text-lg">No invoices yet</p>
          <p className="text-gray-400 text-sm mt-1">Invoices will appear here after you submit an order.</p>
        </div>
      )}

      {/* Table (desktop) */}
      {filtered.length > 0 && (
        <>
          <div className="hidden md:block card overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="px-3 py-3 text-center font-semibold text-gray-400 w-8"></th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-400">Invoice #</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-400">Order</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-400">Sidemark</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-400">Date</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-400">Total</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-400">Balance Due</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-400">Pay Amount</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-400">Status</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-400"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(order => {
                  const balance = getBalanceDue(order);
                  const isSelected = selected[order.id] != null;
                  const canSelect = !isPaid(order) && balance > 0.5;
                  const invoiceDate = order.invoiceData
                    ? (typeof order.invoiceData === 'string' ? JSON.parse(order.invoiceData) : order.invoiceData).invoiceDate
                    : order.createdAt?.split('T')[0];
                  return (
                    <tr key={order.id} className={isSelected ? 'bg-yellow-50/5' : ''}>
                      <td className="px-3 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!canSelect}
                          onChange={() => toggleSelected(order)}
                          className="rounded cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                          title={canSelect ? 'Select for batch payment' : 'No balance due'}
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-white">
                        {order.invoiceNumber || `INV-${order.orderNumber}`}
                        {order.invoiceAdjusted && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">Adjusted</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400">{order.orderNumber}</td>
                      <td className="px-4 py-3 text-gray-400">{order.sidemark || '-'}</td>
                      <td className="px-4 py-3 text-gray-400">{invoiceDate}</td>
                      <td className="px-4 py-3 text-right text-white font-medium">{fmt(getInvoiceTotal(order))}</td>
                      <td className="px-4 py-3 text-right font-semibold" style={{ color: balance > 0.5 ? '#d97706' : '#059669' }}>
                        {fmt(balance)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isSelected ? (
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-gray-400">$</span>
                            <input
                              type="number"
                              min="1"
                              max={balance.toFixed(2)}
                              step="0.01"
                              value={selected[order.id]}
                              onChange={(e) => updateSelectedAmount(order.id, e.target.value)}
                              className="w-24 text-right bg-transparent border rounded px-2 py-1 text-white font-mono text-sm"
                              style={{ borderColor: 'var(--border-color)' }}
                            />
                          </div>
                        ) : (
                          <span className="text-gray-500 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">{renderStatusBadge(order)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleDownloadPdf(order.id, order.invoiceNumber || `INV-${order.orderNumber}`)}
                            disabled={downloadingId === order.id}
                            className="text-sm font-medium transition-colors disabled:opacity-50"
                            style={{ color: '#60a5fa' }}
                            title="Download PDF"
                          >
                            {downloadingId === order.id ? (
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeLinecap="round" /></svg>
                              </span>
                            ) : (
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                            )}
                          </button>
                          <Link
                            to={`/orders/${order.id}`}
                            className="text-sm text-keder-yellow hover:text-yellow-300 font-medium"
                          >
                            View
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Cards (mobile) */}
          <div className="md:hidden space-y-3">
            {filtered.map(order => {
              const balance = getBalanceDue(order);
              const isSelected = selected[order.id] != null;
              const canSelect = !isPaid(order) && balance > 0.5;
              return (
                <div key={order.id} className="card p-4">
                  <div className="flex items-start gap-3 mb-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={!canSelect}
                      onChange={() => toggleSelected(order)}
                      className="mt-1 rounded cursor-pointer disabled:opacity-30"
                    />
                    <Link to={`/orders/${order.id}`} className="flex-1 block">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-white">{order.invoiceNumber || `INV-${order.orderNumber}`}</span>
                        {renderStatusBadge(order)}
                      </div>
                      <div className="text-sm text-gray-500 mb-1">{order.orderNumber} {order.sidemark ? `- ${order.sidemark}` : ''}</div>
                      <div className="flex justify-between text-sm mt-2">
                        <span className="text-gray-500">Total: {fmt(getInvoiceTotal(order))}</span>
                        <span className="font-semibold" style={{ color: balance > 0.5 ? '#d97706' : '#059669' }}>
                          Due: {fmt(balance)}
                        </span>
                      </div>
                    </Link>
                  </div>
                  {isSelected && (
                    <div className="flex items-center gap-2 mt-2 mb-1">
                      <label className="text-xs text-gray-400">Pay:</label>
                      <span className="text-gray-400">$</span>
                      <input
                        type="number"
                        min="1"
                        max={balance.toFixed(2)}
                        step="0.01"
                        value={selected[order.id]}
                        onChange={(e) => updateSelectedAmount(order.id, e.target.value)}
                        className="flex-1 text-right bg-transparent border rounded px-2 py-1 text-white font-mono text-sm"
                        style={{ borderColor: 'var(--border-color)' }}
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
                    <button
                      onClick={() => handleDownloadPdf(order.id, order.invoiceNumber || `INV-${order.orderNumber}`)}
                      disabled={downloadingId === order.id}
                      className="flex items-center gap-1.5 text-sm font-medium disabled:opacity-50"
                      style={{ color: '#60a5fa' }}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      {downloadingId === order.id ? 'Generating...' : 'Download PDF'}
                    </button>
                    <Link to={`/orders/${order.id}`} className="text-sm text-keder-yellow hover:text-yellow-300 font-medium">
                      View
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {filtered.length === 0 && orders.length > 0 && (
        <div className="card p-8 text-center">
          <p className="text-gray-500">No invoices match this filter.</p>
        </div>
      )}

      {/* Add bottom padding so sticky pay bar doesn't cover last row */}
      {selectedCount > 0 && <div style={{ height: 96 }} />}

      {/* Sticky pay bar */}
      {selectedCount > 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 border-t shadow-lg"
          style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-between">
            <div>
              <div className="text-sm text-gray-400">
                {selectedCount} invoice{selectedCount === 1 ? '' : 's'} selected
              </div>
              <div className="text-xl font-bold text-white font-mono">{fmt(selectedTotal)}</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelected({})}
                disabled={paying}
                className="px-4 py-2 text-sm rounded font-medium border disabled:opacity-50"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
              >
                Clear
              </button>
              <button
                onClick={handlePaySelected}
                disabled={paying || selectedTotal < 1}
                className="px-5 py-2 text-sm rounded font-semibold bg-keder-yellow text-black hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {`Pay ${fmt(selectedTotal)}`}
              </button>
            </div>
          </div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-2 text-[11px] text-gray-500">
            Pay by card or bank (ACH) on the next screen · Each invoice appears as its own line on your Stripe receipt
          </div>
        </div>
      )}

      {showCardModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
          onClick={(e) => { if (e.target === e.currentTarget && !paying) setShowCardModal(false); }}
        >
          <div className="card w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Pay {selectedCount} invoice{selectedCount === 1 ? '' : 's'}</h2>
              <button
                onClick={() => setShowCardModal(false)}
                disabled={paying}
                className="text-gray-400 hover:text-white disabled:opacity-50"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="mb-4 text-sm space-y-1">
              <div className="flex justify-between text-gray-400">
                <span>Selected subtotal</span>
                <span className="font-mono text-white">{fmt(selectedTotal)}</span>
              </div>
              {availableCredit > 0 && (
                <div className="rounded border p-3 mt-2" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">Available credit on file</span>
                    <span className="font-mono text-green-400">{fmt(availableCredit)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <label className="text-xs text-gray-400 whitespace-nowrap">Apply $</label>
                    <input
                      type="number"
                      min={0}
                      max={Math.min(availableCredit, Math.max(0, selectedTotal - 1))}
                      step={0.01}
                      value={creditToApply}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        const safe = isNaN(v) ? 0 : Math.max(0, Math.min(v, availableCredit, Math.max(0, selectedTotal - 1)));
                        setCreditToApply(Math.round(safe * 100) / 100);
                      }}
                      className="flex-1 text-right bg-transparent border rounded px-2 py-1 text-white font-mono text-sm"
                      style={{ borderColor: 'var(--border-color)' }}
                    />
                    <button
                      type="button"
                      onClick={() => setCreditToApply(0)}
                      className="text-xs text-gray-500 underline"
                    >Clear</button>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">At least $1.00 must remain for the card charge.</p>
                </div>
              )}
              {creditToApply > 0 && (
                <div className="flex justify-between text-gray-400 pt-1">
                  <span>Credit applied</span>
                  <span className="font-mono text-green-400">−{fmt(creditToApply)}</span>
                </div>
              )}
              <div className="flex justify-between text-white font-semibold pt-2 mt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                <span>To charge to card</span>
                <span className="font-mono">{fmt(Math.max(0, selectedTotal - creditToApply))}</span>
              </div>
            </div>
            {savedCards.length > 0 && (
              <div className="mb-4">
                <div className="text-xs text-gray-400 mb-2">Saved payment methods</div>
                <div className="space-y-2">
                  {savedCards.map(pm => {
                    const isBank = pm.type === 'us_bank_account';
                    return (
                      <button
                        key={pm.id}
                        disabled={chargingSavedCardId !== null}
                        onClick={async () => {
                          setPayError(null);
                          setChargingSavedCardId(pm.id);
                          try {
                            const { loadStripe } = await import('@stripe/stripe-js');
                            const pk = (await paymentApi.getStripePublicKey()).publishableKey;
                            const stripe = await loadStripe(pk);
                            if (!stripe) throw new Error('Stripe failed to load');
                            const intent = await paymentApi.createMultiInvoiceIntent({
                              payments: buildPaymentsPayload(),
                              paymentMethodId: pm.id,
                              // For bank, funding is irrelevant (no surcharge on ACH).
                              // For card, backend re-verifies funding from Stripe regardless.
                              cardFunding: isBank ? undefined : 'credit',
                              creditApplied: creditToApply > 0 ? creditToApply : undefined,
                            });
                            if (!intent.success || !intent.clientSecret) {
                              setPayError(intent.error || 'Could not start payment');
                              return;
                            }
                            // Branch on PM type — different confirm method.
                            const confirmResult = isBank
                              ? await stripe.confirmUsBankAccountPayment(intent.clientSecret, { payment_method: pm.id })
                              : await stripe.confirmCardPayment(intent.clientSecret, { payment_method: pm.id });
                            const { paymentIntent, error } = confirmResult;
                            if (error) {
                              setPayError(error.message || 'Payment was declined');
                              return;
                            }
                            if (!paymentIntent) {
                              setPayError('No payment intent returned');
                              return;
                            }
                            // ACH lands in 'processing' — money will clear in 3-5 business
                            // days. Card lands in 'succeeded' immediately.
                            if (paymentIntent.status !== 'succeeded' && paymentIntent.status !== 'processing') {
                              setPayError(`Payment ${paymentIntent.status} — please try again`);
                              return;
                            }
                            setShowCardModal(false);
                            setSelected({});
                            setCreditToApply(0);
                            setSuccessBanner(
                              isBank
                                ? 'Bank payment submitted — money typically clears in 3-5 business days.'
                                : 'Payment received — refreshing invoices…'
                            );
                            setTimeout(() => {
                              fetchInvoices();
                              dealerCreditApi.getMyAvailable().then(r => setAvailableCredit(Number(r?.available || 0))).catch(() => {});
                            }, 2000);
                          } catch (err: any) {
                            setPayError(err?.message || 'Payment failed');
                          } finally {
                            setChargingSavedCardId(null);
                          }
                        }}
                        className="w-full flex items-center justify-between rounded border p-3 hover:bg-gray-800/50 disabled:opacity-50"
                        style={{ borderColor: 'var(--border-color)' }}
                      >
                        <div className="text-left flex items-center gap-3">
                          <span className="text-xs uppercase px-2 py-0.5 rounded font-mono"
                                style={{ background: isBank ? 'rgba(16,185,129,0.15)' : 'rgba(99,91,255,0.15)',
                                         color: isBank ? '#10b981' : '#a5a0ff' }}>
                            {isBank ? 'ACH' : 'Card'}
                          </span>
                          <div>
                            <div className="text-sm text-white font-medium">
                              {(pm.brand || (isBank ? 'Bank' : 'Card')).toUpperCase()} ···· {pm.last4}
                            </div>
                            <div className="text-xs text-gray-500">
                              {isBank
                                ? `${pm.accountType || 'Checking'} · Clears in 3-5 days`
                                : `Exp ${String(pm.expMonth).padStart(2, '0')}/${String(pm.expYear).slice(-2)}`}
                            </div>
                          </div>
                        </div>
                        <span className="text-keder-yellow text-sm font-semibold whitespace-nowrap">
                          {chargingSavedCardId === pm.id ? 'Processing…' : `Pay ${fmt(Math.max(0, selectedTotal - creditToApply))}`}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {!showNewCardForm && (
                  <button
                    onClick={() => setShowNewCardForm(true)}
                    className="mt-3 text-xs text-gray-400 underline hover:text-white"
                  >
                    + Use a different card
                  </button>
                )}
              </div>
            )}

            {/* ACH option — redirects to Stripe Checkout for bank verification.
                Works whether or not the dealer has a saved bank PM on file. */}
            <button
              onClick={async () => {
                setPayError(null);
                setPaying(true);
                try {
                  const result = await paymentApi.createMultiInvoiceCheckout({
                    payments: buildPaymentsPayload(),
                    paymentMethodType: 'us_bank_account',
                  });
                  if (!result.success || !result.checkoutUrl) {
                    setPayError(result.error || 'Failed to start ACH payment');
                    return;
                  }
                  window.location.href = result.checkoutUrl;
                } catch (err: any) {
                  setPayError(err?.response?.data?.message || err?.message || 'Failed to start ACH payment');
                } finally {
                  setPaying(false);
                }
              }}
              disabled={paying || chargingSavedCardId !== null}
              className="w-full mb-4 py-3 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
              style={{ background: '#10b981', color: '#fff' }}
            >
              {paying ? 'Starting…' : `Pay with Bank (ACH) — ${fmt(Math.max(0, selectedTotal - creditToApply))}`}
            </button>
            <div className="text-[11px] text-gray-500 mb-3 text-center">
              Bank takes 3-5 business days to clear · No processing fee
            </div>

            {(savedCards.length === 0 || showNewCardForm) && (
              <StripeCardForm
                baseAmount={Math.max(0, selectedTotal - creditToApply)}
                disclosure={surchargeEnabled ? "Credit card payments include a 3% processing fee. Debit cards are not surcharged. The exact total appears below after your card is verified." : undefined}
                submitLabel={`Pay with Card`}
                createIntent={async ({ paymentMethodId, cardFunding }) => {
                  setPaying(true);
                  try {
                    return await paymentApi.createMultiInvoiceIntent({
                      payments: buildPaymentsPayload(),
                      paymentMethodId,
                      cardFunding,
                      creditApplied: creditToApply > 0 ? creditToApply : undefined,
                    });
                  } finally {
                    setPaying(false);
                  }
                }}
                onSuccess={() => {
                  setShowCardModal(false);
                  setSelected({});
                  setCreditToApply(0);
                  setSuccessBanner('Payment received — refreshing invoices…');
                  setTimeout(() => {
                    fetchInvoices();
                    dealerCreditApi.getMyAvailable().then(r => setAvailableCredit(Number(r?.available || 0))).catch(() => {});
                  }, 2000);
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
